// ---------------------------------------------------------------------------
// Flow Analysis — state usage, navigation edges, flow map entries.
//
// Pure functions for analyzing CogoFlow step trees.
// Used by cross-reference index and visualization layers.
// ---------------------------------------------------------------------------

import type { Flow, FlowStep } from '@cogo/types-v2'
import type { CrossReferenceIndex, FlowUsageEntry } from '@/lib/cross-reference'
import { RUNTIME_REFS } from '@/lib/action-constants'

// ---------------------------------------------------------------------------
// walkFlowSteps — shared recursive step visitor
// ---------------------------------------------------------------------------

/**
 * Recursively walk all steps in a flow, calling `fn` for each step.
 * Handles basic, conditional, loop, parallel, tryCatch, switch, expression,
 * stage, and saga.
 */
export function walkFlowSteps(
  steps: FlowStep[],
  fn: (step: FlowStep) => void,
): void {
  for (const step of steps) {
    fn(step)
    switch (step.type) {
      case 'conditional':
        walkFlowSteps(step.then, fn)
        if (step.else) walkFlowSteps(step.else, fn)
        break
      case 'loop':
        walkFlowSteps(step.steps, fn)
        break
      case 'parallel':
        walkFlowSteps(step.steps, fn)
        break
      case 'tryCatch':
        walkFlowSteps(step.try, fn)
        if (step.catch) walkFlowSteps(step.catch, fn)
        if (step.finally) walkFlowSteps(step.finally, fn)
        break
      case 'switch':
        for (const caseSteps of Object.values(step.cases)) {
          walkFlowSteps(caseSteps, fn)
        }
        if (step.default) walkFlowSteps(step.default, fn)
        break
      case 'stage':
        if (step.onEnter) walkFlowSteps(step.onEnter, fn)
        walkFlowSteps(step.steps, fn)
        if (step.onSuccess) walkFlowSteps(step.onSuccess, fn)
        if (step.onError) walkFlowSteps(step.onError, fn)
        if (step.onFinally) walkFlowSteps(step.onFinally, fn)
        if (step.compensate) walkFlowSteps(step.compensate, fn)
        break
      case 'saga':
        walkFlowSteps(step.stages, fn)
        break
      // basic, expression: no children
    }
  }
}

// ---------------------------------------------------------------------------
// extractExpressionRefs
// ---------------------------------------------------------------------------

/**
 * Extract root variable names from `#{var}` or `#{var.path}` expression patterns.
 * Filters out runtime-scoped refs (response, event, item, index, error, etc.)
 * and numeric-start tokens.
 */
export function extractExpressionRefs(text: string): string[] {
  if (!text) return []

  const seen = new Set<string>()
  const results: string[] = []
  const regex = /#\{([^}]+)\}/g
  let match: RegExpExecArray | null

  while ((match = regex.exec(text)) !== null) {
    const inner = match[1].trim()
    // Extract root name — split on `.`, spaces, operators
    const root = inner.split(/[.\s+\-*/()[\],?:!<>=&|]/)[0]
    if (!root) continue
    // Filter runtime refs and numeric starts
    if (RUNTIME_REFS.has(root)) continue
    if (/^\d/.test(root)) continue
    if (!seen.has(root)) {
      seen.add(root)
      results.push(root)
    }
  }

  return results
}

// ---------------------------------------------------------------------------
// analyzeFlowStateUsage
// ---------------------------------------------------------------------------

export interface StateUsageEntry {
  key: string
  mode: 'read' | 'write'
  stepAction: string
}

const WRITE_ACTIONS = new Set(['set_state_action', 'setState', 'set_property_action'])

/**
 * Analyze a flow for state variable read/write usage.
 * Writes: set_state_action / setState / set_property_action with
 *   params.target (canonical, runtime-v2 reads STRICT params.target) or
 *   params.key (legacy alias, kept so pre-migration steps stay counted)
 * Reads: any #{varName} expression ref in step params
 */
export function analyzeFlowStateUsage(flow: Flow): StateUsageEntry[] {
  const entries: StateUsageEntry[] = []

  walkFlowSteps(flow.steps, (step) => {
    if (step.type === 'basic' || step.type === 'expression') {
      // Check for writes — canonical param first, legacy alias fallback
      const writeKey = step.params?.target ?? step.params?.key
      if (WRITE_ACTIONS.has(step.action) && writeKey) {
        entries.push({
          key: String(writeKey),
          mode: 'write',
          stepAction: step.action,
        })
      }

      // Check for reads via expression refs in all params
      const paramsStr = JSON.stringify(step.params ?? {})
      const refs = extractExpressionRefs(paramsStr)
      for (const ref of refs) {
        entries.push({ key: ref, mode: 'read', stepAction: step.action })
      }
    }

    // Also check condition expressions in conditional/switch
    if (step.type === 'conditional') {
      const condRefs = extractExpressionRefs(step.condition)
      for (const ref of condRefs) {
        entries.push({ key: ref, mode: 'read', stepAction: 'conditional' })
      }
    }
    if (step.type === 'switch') {
      const valRefs = extractExpressionRefs(step.value)
      for (const ref of valRefs) {
        entries.push({ key: ref, mode: 'read', stepAction: 'switch' })
      }
    }
  })

  return entries
}

// ---------------------------------------------------------------------------
// extractNavigationEdges
// ---------------------------------------------------------------------------

export interface NavigationEntry {
  targetRoute: string
  targetPageId: string | null
  flowName: string
}

const NAV_ACTIONS = new Set(['navigation_action', 'navigate'])

/**
 * Extract navigation edges from a flow.
 * Matches navigation_action/navigate steps to page routes.
 * Handles dynamic segments: `#{expr}` → `:param` for matching.
 */
export function extractNavigationEdges(
  flowName: string,
  flow: Flow,
  pages: Array<{ id: string; route: string }>,
): NavigationEntry[] {
  const entries: NavigationEntry[] = []

  walkFlowSteps(flow.steps, (step) => {
    if ((step.type === 'basic' || step.type === 'expression') && NAV_ACTIONS.has(step.action)) {
      const route = step.params?.route
      if (typeof route !== 'string') return

      // Normalize dynamic segments for matching: #{expr} → :param
      const normalized = route.replace(/#\{[^}]+\}/g, ':param')

      // Find matching page
      let matchedPage: { id: string; route: string } | undefined
      for (const page of pages) {
        // Normalize page route pattern: :paramName → :param
        const pageNorm = page.route.replace(/:[^/]+/g, ':param')
        if (pageNorm === normalized) {
          matchedPage = page
          break
        }
      }

      entries.push({
        targetRoute: route,
        targetPageId: matchedPage?.id ?? null,
        flowName,
      })
    }
  })

  return entries
}

// ---------------------------------------------------------------------------
// buildFlowMapEntries
// ---------------------------------------------------------------------------

export interface FlowMapEntry {
  blockName: string
  elementId: string
  eventType: string
  flowName: string
}

/**
 * Flatten the CrossReferenceIndex flowUsageIndex into an array of FlowMapEntry.
 */
export function buildFlowMapEntries(crossRef: CrossReferenceIndex): FlowMapEntry[] {
  const entries: FlowMapEntry[] = []

  for (const [flowName, usages] of crossRef.flowUsageIndex) {
    for (const usage of usages) {
      entries.push({
        blockName: usage.blockName,
        elementId: usage.elementId,
        eventType: usage.eventType,
        flowName,
      })
    }
  }

  return entries
}
